import { HugeiconsIcon } from '@hugeicons/react';
import { PencilEdit02Icon, ViewIcon } from '@hugeicons/core-free-icons';
import { useId, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// MarkdownEditor — editor Markdown reutilizable con dos modos: "Escribir"
// (Textarea controlada) y "Vista previa" (ReactMarkdown + remarkGfm). Guarda
// SIEMPRE Markdown crudo (nunca HTML): el padre recibe el texto tal cual se
// escribe mediante onChange. El preview replica las clases prose de marca que
// usa la pantalla de chat (via/frontend/src/pages/Insights.jsx) para que el
// render sea consistente con lo que el usuario final verá.
//
// Props:
//   id          identificador base para asociar label y controles (htmlFor).
//   label       etiqueta visible del campo.
//   description descripción opcional bajo la etiqueta.
//   value       Markdown crudo controlado.
//   onChange    callback(nuevoTexto) con el Markdown crudo.
//   rows        filas visibles del Textarea (por defecto 6).
//   maxLength   límite de caracteres opcional.
//   placeholder placeholder del Textarea.
export default function MarkdownEditor({
  id,
  label,
  description,
  value,
  onChange,
  rows = 6,
  maxLength,
  placeholder,
}) {
  const [mode, setMode] = useState('write'); // 'write' | 'preview'
  // useId garantiza ids únicos aunque el consumidor no pase uno explícito.
  const autoId = useId();
  const fieldId = id || autoId;
  const text = value || '';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={fieldId} className="text-sm font-medium text-foreground">
            {label}
          </label>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>

        {/* Toggle Escribir | Vista previa: botones operables por teclado con
            estado activo expuesto vía aria-pressed. */}
        <div
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-transparent p-0.5"
          role="group"
          aria-label="Modo del editor">
          <button
            type="button"
            onClick={() => setMode('write')}
            aria-pressed={mode === 'write'}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === 'write'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}>
            <HugeiconsIcon icon={PencilEdit02Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
            Escribir
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            aria-pressed={mode === 'preview'}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === 'preview'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}>
            <HugeiconsIcon icon={ViewIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
            Vista previa
          </button>
        </div>
      </div>

      {mode === 'write' ? (
        <Textarea
          id={fieldId}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
        />
      ) : (
        // Contenedor del preview con mínimo de alto equivalente al Textarea para
        // evitar saltos de layout al alternar de modo.
        <div
          className="prose prose-sm max-w-none rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-relaxed text-text
            prose-headings:text-text prose-headings:font-semibold
            prose-p:text-text prose-strong:text-text prose-li:text-text
            prose-table:text-text prose-code:text-text"
          style={{ minHeight: `${rows * 1.5 + 1}rem` }}>
          {text.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
          ) : (
            <p className="not-prose text-sm italic text-muted-foreground">Nada que previsualizar aún.</p>
          )}
        </div>
      )}
    </div>
  );
}
